import { useMemo, useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getPolicyAnalysis } from '../../../infrastructure/apiClient';
import CardTable from '../../../components/Table/CardTable';
import { buildFilterOptions, getCheckboxFilterProps } from '../../../components/Table/columnFilterUtils';
import type { PolicyAnalysisDTO } from '../../../infrastructure/models';
import { POLICY_CONFIGURATION_BUILDING_BLOCKS } from '../constants/buildingBlocks';
import DashboardLayout, { dashboardStyles as styles } from '../../../components/DashboardLayout/DashboardLayout';
import { useDashboardData } from '../../../hooks/useDashboardData';
import { sortData, sortConfig, type SortConfig } from '../../../utils/sortUtils';

interface AnalysisDashboardProps {
	onBack?: () => void;
	onViewDetails?: (comparison: PolicyAnalysisDTO) => void;
}

const BUILDING_BLOCK_LABEL_BY_CODE = POLICY_CONFIGURATION_BUILDING_BLOCKS.reduce<Record<string, string>>((acc, block) => {
	acc[block.code] = block.label;
	return acc;
}, {});

const STATUS_COLORS: Record<string, string> = {
	Pending: 'gold',
	Completed: 'green',
	Failed: 'red',
};

const getConfigurationTypeLabel = (code?: string | null) => {
	if (!code) {
		return '—';
	}
	return BUILDING_BLOCK_LABEL_BY_CODE[code] ?? code;
};

const formatRegionLabel = ({ continent, country, state }: Pick<PolicyAnalysisDTO, 'continent' | 'country' | 'state'>) => {
	const parts = [continent, country, state].filter((part): part is string => Boolean(part && part.trim()));
	return parts.length ? parts.join(' / ') : '—';
};

const SORT_CONFIGS: SortConfig<PolicyAnalysisDTO>[] = [
	sortConfig('comparisonName', 'string'),
	sortConfig('configurationType', 'string', (record) => getConfigurationTypeLabel(record.configurationType)),
	sortConfig('configurationSubtype', 'string'),
	sortConfig('region', 'string', (record) => formatRegionLabel(record)),
	sortConfig('status', 'string'),
	sortConfig('createdBy', 'string'),
	sortConfig('createdAt', 'date'),
];

const AnalysisDashboard = ({ onBack, onViewDetails }: AnalysisDashboardProps) => {
	const {
		data: analysis,
		loading,
		error,
		activeClient,
		tenantsLoading,
		pagination,
		sorter,
		tablePagination,
		handleTableChange,
		fetchData: fetchAnalysis,
		abortControllerRef,
		disableActions,
	} = useDashboardData<PolicyAnalysisDTO>({
		fetchFn: getPolicyAnalysis,
		entityName: 'policy analysis',
	});
	const [refreshing, setRefreshing] = useState(false);

	const [comparisonNameFilters, setComparisonNameFilters] = useState<string[]>([]);
	const [configTypeFilters, setConfigTypeFilters] = useState<string[]>([]);
	const [subTypeFilters, setSubTypeFilters] = useState<string[]>([]);
	const [regionFilters, setRegionFilters] = useState<string[]>([]);
	const [statusFilters, setStatusFilters] = useState<string[]>([]);
	const [createdByFilters, setCreatedByFilters] = useState<string[]>([]);

	const comparisonNameOptions = useMemo(() => buildFilterOptions(analysis, (r) => r.comparisonName ?? undefined), [analysis]);
	const configTypeOptions = useMemo(() => buildFilterOptions(analysis, (r) => getConfigurationTypeLabel(r.configurationType) !== '—' ? getConfigurationTypeLabel(r.configurationType) : undefined), [analysis]);
	const subTypeOptions = useMemo(() => buildFilterOptions(analysis, (r) => r.configurationSubtype ?? undefined), [analysis]);
	const regionOptions = useMemo(() => buildFilterOptions(analysis, (r) => { const label = formatRegionLabel(r); return label !== '—' ? label : undefined; }), [analysis]);
	const statusOptions = useMemo(() => buildFilterOptions(analysis, (r) => r.status ?? undefined), [analysis]);
	const createdByOptions = useMemo(() => buildFilterOptions(analysis, (r) => r.createdBy ?? undefined), [analysis]);

	const columns = useMemo<ColumnsType<PolicyAnalysisDTO>>(
		() => [
			{
				title: 'Comparison Name',
				key: 'comparisonName',
				sorter: (a, b) => (a.comparisonName ?? '').localeCompare(b.comparisonName ?? ''),
				sortOrder: sorter.columnKey === 'comparisonName' ? sorter.order : null,
				...getCheckboxFilterProps(
					comparisonNameFilters,
					setComparisonNameFilters,
					comparisonNameOptions,
					(record) => record.comparisonName ?? undefined,
				),
				render: (_value, record) => (
					<button
						type="button"
						className={styles.nameLink}
						onClick={() => onViewDetails?.(record)}
						disabled={!onViewDetails}
					>
						{record.comparisonName || 'View comparison'}
					</button>
				),
			},
			{
				title: 'Configuration Type',
				dataIndex: 'configurationType',
				key: 'configurationType',
				ellipsis: true,
				sorter: (a, b) => getConfigurationTypeLabel(a.configurationType).localeCompare(getConfigurationTypeLabel(b.configurationType)),
				sortOrder: sorter.columnKey === 'configurationType' ? sorter.order : null,
				...getCheckboxFilterProps(
					configTypeFilters,
					setConfigTypeFilters,
					configTypeOptions,
					(record) => getConfigurationTypeLabel(record.configurationType) !== '—' ? getConfigurationTypeLabel(record.configurationType) : undefined,
				),
				render: (value?: string) => getConfigurationTypeLabel(value),
			},
			{
				title: 'Sub-Type',
				dataIndex: 'configurationSubtype',
				key: 'configurationSubtype',
				ellipsis: true,
				sorter: (a, b) => (a.configurationSubtype ?? '').localeCompare(b.configurationSubtype ?? ''),
				sortOrder: sorter.columnKey === 'configurationSubtype' ? sorter.order : null,
				...getCheckboxFilterProps(
					subTypeFilters,
					setSubTypeFilters,
					subTypeOptions,
					(record) => record.configurationSubtype ?? undefined,
				),
			},
			{
				title: 'Region',
				key: 'region',
				ellipsis: true,
				sorter: (a, b) => formatRegionLabel(a).localeCompare(formatRegionLabel(b)),
				sortOrder: sorter.columnKey === 'region' ? sorter.order : null,
				...getCheckboxFilterProps(
					regionFilters,
					setRegionFilters,
					regionOptions,
					(record) => { const label = formatRegionLabel(record); return label !== '—' ? label : undefined; },
				),
				render: (_value, record) => formatRegionLabel(record),
			},
			{
				title: 'Status',
				dataIndex: 'status',
				key: 'status',
				ellipsis: true,
				sorter: (a, b) => (a.status ?? '').localeCompare(b.status ?? ''),
				sortOrder: sorter.columnKey === 'status' ? sorter.order : null,
				...getCheckboxFilterProps(
					statusFilters,
					setStatusFilters,
					statusOptions,
					(record) => record.status ?? undefined,
				),
				render: (status?: string) => (
					<Tag color={STATUS_COLORS[status ?? ''] || 'default'} className="!mr-0">
						{status ?? '—'}
					</Tag>
				),
			},
			{
				title: 'Requested By',
				dataIndex: 'createdBy',
				key: 'createdBy',
				ellipsis: true,
				sorter: (a, b) => (a.createdBy ?? '').localeCompare(b.createdBy ?? ''),
				sortOrder: sorter.columnKey === 'createdBy' ? sorter.order : null,
				...getCheckboxFilterProps(
					createdByFilters,
					setCreatedByFilters,
					createdByOptions,
					(record) => record.createdBy ?? undefined,
				),
			},
			{
				title: 'Requested On',
				dataIndex: 'createdAt',
				key: 'createdAt',
				ellipsis: true,
				sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
				sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null,
				render: (value: string) => dayjs(value).format('MMM D, YYYY h:mm A'),
			},
		],
		[onViewDetails, sorter.columnKey, sorter.order,
		 comparisonNameFilters, comparisonNameOptions,
		 configTypeFilters, configTypeOptions,
		 subTypeFilters, subTypeOptions,
		 regionFilters, regionOptions,
		 statusFilters, statusOptions,
		 createdByFilters, createdByOptions],
	);

	const sortedAnalysis = useMemo(() => sortData(analysis, sorter, SORT_CONFIGS), [analysis, sorter]);

	return (
		<DashboardLayout
			title="Policy Analysis Dashboard"
			actions={
			<>
				{onBack && (
					<Button onClick={onBack} type="default">
						Back to Policy Uploads
					</Button>
				)}
				<Button
					icon={<ReloadOutlined />}
					onClick={async () => {
						if (!activeClient?.id) return;
						abortControllerRef.current?.abort();
						const controller = new AbortController();
						abortControllerRef.current = controller;
						setRefreshing(true);
						try {
							await fetchAnalysis(activeClient.id, {
								offset: 0,
								limit: pagination.pageSize,
								signal: controller.signal,
							});
						} finally {
							if (!controller.signal.aborted) {
								setRefreshing(false);
							}
						}
					}}
					disabled={disableActions || refreshing}
					loading={refreshing}
				>
					Refresh
				</Button>
			</>
		}
		>
			<CardTable<PolicyAnalysisDTO>
				title="Policy Analysis"
				columns={columns}
				dataSource={sortedAnalysis}
				rowKey={(record) => record.comparisonId}
				loading={loading || tenantsLoading}
				pagination={tablePagination}
				onChange={handleTableChange}
				error={error}
				emptyText={disableActions ? 'Select a tenant to view policy analysis.' : 'No policy analysis found.'}
				className={styles.cardTable}
			/>
		</DashboardLayout>
	);
};

export default AnalysisDashboard;
